import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, MapPin, MessageCircle, PhoneCall, ShieldCheck, TriangleAlert, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONTACT, DEFAULT_WHATSAPP_MESSAGE, getWhatsAppUrl } from "./config";
import { Footer, MobileEmergencyBar } from "./RequestFooter";
import { Navbar } from "./Navbar";
import { EditableText } from "./TextEditor";

const BASE_URL = "https://viscar.it";
const seoHeroImage = "https://static.prod-images.emergentagent.com/jobs/8ad3288b-acff-428d-904d-27a1bd011141/images/d8ee79877f0c3d607276638b737a2e43261f48b7b8e76e2611cc01b84b306ee0.png";

const upsertStructuredData = (id, payload) => {
  let tag = document.head.querySelector(`#${id}`);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(payload);
};

const removeStructuredData = (id) => {
  document.head.querySelector(`#${id}`)?.remove();
};

export const LOCAL_SEO_LINKS = [
  {
    slug: "carroattrezzi-messina",
    path: "/carroattrezzi-messina",
    label: "Carroattrezzi Messina",
    badge: "CARROATTREZZI",
    description: "Accesso dedicato al servizio carroattrezzi con intervento rapido, traino e supporto H24 nell’area di Messina.",
  },
  {
    slug: "soccorso-stradale-messina",
    path: "/soccorso-stradale-messina",
    label: "Soccorso stradale Messina",
    badge: "SOCCORSO H24",
    description: "Percorso dedicato al soccorso stradale su Messina, Tremestieri e viabilità urbana con accesso diretto ai contatti.",
  },
  {
    slug: "soccorso-autostradale-a18-a20",
    path: "/soccorso-autostradale-a18-a20",
    label: "Soccorso autostradale A18 A20",
    badge: "AUTOSTRADA",
    description: "Approfondimento dedicato agli interventi in autostrada su A18 e A20 con riferimenti operativi chiari.",
  },
  {
    slug: "soccorso-mezzi-pesanti-messina",
    path: "/soccorso-mezzi-pesanti-messina",
    label: "Soccorso mezzi pesanti Messina",
    badge: "HEAVY RECOVERY",
    description: "Percorso dedicato a camion, autobus, motrici e semirimorchi con heavy recovery e supporto tecnico.",
  },
];

export const LOCAL_AREA_LINKS = [
  {
    slug: "soccorso-stradale-provincia-di-messina",
    path: "/soccorso-stradale-provincia-di-messina",
    label: "Messina e provincia",
    badge: "TERRITORIO",
    description: "Copertura dedicata alla provincia di Messina con riferimenti operativi chiari e accesso rapido ai contatti.",
  },
  {
    slug: "carroattrezzi-tremestieri",
    path: "/carroattrezzi-tremestieri",
    label: "Carroattrezzi Tremestieri",
    badge: "SVINCOLO",
    description: "Approfondimento dedicato all’area di Tremestieri e ai collegamenti rapidi con viabilità urbana e autostrada.",
  },
  {
    slug: "soccorso-stradale-milazzo",
    path: "/soccorso-stradale-milazzo",
    label: "Soccorso stradale Milazzo",
    badge: "AREA NORD",
    description: "Percorso dedicato a chi cerca assistenza, recovery e supporto nell’area di Milazzo e nei collegamenti limitrofi.",
  },
  {
    slug: "soccorso-stradale-taormina",
    path: "/soccorso-stradale-taormina",
    label: "Soccorso stradale Taormina",
    badge: "AREA SUD",
    description: "Pagina dedicata ai collegamenti tra Messina, Taormina e le tratte più sensibili verso l’autostrada.",
  },
  {
    slug: "carroattrezzi-villafranca-tirrena",
    path: "/carroattrezzi-villafranca-tirrena",
    label: "Carroattrezzi Villafranca Tirrena",
    badge: "AREA OVEST",
    description: "Percorso dedicato a Villafranca Tirrena e ai collegamenti con Messina per richieste di carroattrezzi e assistenza H24.",
  },
];

export const LOCAL_SEO_KEYWORDS = {
  shared: [
    "soccorso stradale messina",
    "soccorso stradale provincia di messina",
    "carroattrezzi messina",
    "carroattrezzi provincia di messina",
    "carro attrezzi messina",
    "carro attrezzi provincia di messina",
    "carroattrezzi villafranca tirrena",
    "carro attrezzi villafranca tirrena",
    "soccorso autostradale a18",
    "soccorso autostradale a20",
    "soccorso mezzi pesanti messina",
    "soccorso stradale milazzo",
    "soccorso stradale taormina",
    "carroattrezzi tremestieri",
  ],
  "carroattrezzi-messina": [
    "carroattrezzi messina",
    "carro attrezzi messina",
    "carroattrezzi provincia di messina",
    "carro attrezzi provincia di messina",
  ],
  "soccorso-stradale-messina": [
    "soccorso stradale messina",
    "soccorso stradale provincia di messina",
    "soccorso stradale tremestieri",
  ],
  "soccorso-autostradale-a18-a20": [
    "soccorso autostradale a18",
    "soccorso autostradale a20",
    "carroattrezzi a18 a20",
  ],
  "soccorso-mezzi-pesanti-messina": [
    "soccorso mezzi pesanti messina",
    "soccorso camion messina",
    "heavy recovery messina",
  ],
  "soccorso-stradale-provincia-di-messina": [
    "soccorso stradale provincia di messina",
    "carroattrezzi provincia di messina",
    "soccorso stradale messina provincia",
  ],
  "carroattrezzi-tremestieri": [
    "carroattrezzi tremestieri",
    "soccorso stradale tremestieri",
    "carro attrezzi tremestieri",
  ],
  "carroattrezzi-villafranca-tirrena": [
    "carroattrezzi villafranca tirrena",
    "carro attrezzi villafranca tirrena",
    "soccorso stradale villafranca tirrena",
  ],
  "soccorso-stradale-milazzo": [
    "soccorso stradale milazzo",
    "carroattrezzi milazzo",
    "soccorso autostradale milazzo",
  ],
  "soccorso-stradale-taormina": [
    "soccorso stradale taormina",
    "carroattrezzi taormina",
    "soccorso autostradale taormina",
  ],
};

const LOCAL_SEO_PAGES = {
  "carroattrezzi-messina": {
    slug: "carroattrezzi-messina",
    path: "/carroattrezzi-messina",
    schemaName: "Carroattrezzi a Messina H24",
    serviceType: "Carroattrezzi e traino veicoli",
    eyebrow: "Carroattrezzi Messina",
    title: "Carroattrezzi a Messina con operatività H24",
    description: "Se stai cercando un carroattrezzi a Messina, Viscar gestisce traino, recupero e assistenza stradale H24 con partenza rapida da Tremestieri verso città, tangenziale, A18 e A20.",
    badges: ["Messina", "Tremestieri", "A18 / A20", "H24"],
    reasonsTitle: "Perché questa pagina intercetta ricerche ad alta urgenza",
    reasonsDescription: "Chi cerca carroattrezzi di solito ha bisogno di una risposta immediata, di un numero chiaro e di un’azienda vicina. Qui Viscar si presenta con un messaggio semplice, locale e orientato alla chiamata.",
    highlights: [
      { icon: Clock3, title: "Intervento rapido", description: "Base operativa a Messina utile per chi ha il mezzo fermo in città, a Tremestieri o in ingresso autostradale." },
      { icon: Truck, title: "Traino e recupero", description: "Carroattrezzi per auto, SUV, furgoni e mezzi da lavoro con gestione di guasti, incidenti e veicoli non marcianti." },
      { icon: ShieldCheck, title: "Presidio H24", description: "Messaggio chiaro su disponibilità continua, utile sia per utenti organici sia per campagne Ads a budget controllato." },
      { icon: MapPin, title: "Copertura locale credibile", description: "Copy costruito intorno a Messina, Tremestieri, A18 e A20 per aumentare pertinenza sulle ricerche geolocalizzate." },
    ],
    proofTitle: "Quando conviene chiamare un carroattrezzi a Messina",
    proofDescription: "Auto in panne, guasto improvviso, veicolo incidentato o impossibilità di ripartire: in tutti questi casi è fondamentale trovare una realtà locale che risponda subito e sappia muoversi tra città, svincoli e autostrada.",
    proofBullets: ["Guasto improvviso in città o in tangenziale", "Veicolo incidentato da mettere in sicurezza", "Auto o furgone fermo vicino agli svincoli", "Richiesta di traino verso officina o luogo sicuro"],
    faqs: [
      { question: "Viscar lavora come carroattrezzi a Messina anche di notte?", answer: "Sì, la comunicazione del sito è impostata per intercettare chi cerca un servizio H24 a Messina, Tremestieri, A18 e A20 anche nelle fasce serali e notturne." },
      { question: "Il servizio è utile solo per auto private?", answer: "No. La pagina è costruita per ricerche legate a auto, SUV, furgoni e mezzi da lavoro leggeri che necessitano traino o recupero rapido." },
      { question: "Perché puntare sulla parola chiave carroattrezzi Messina?", answer: "Perché è una ricerca con intenzione molto forte: chi la digita vuole trovare subito un numero affidabile e un operatore realmente vicino." },
      { question: "La pagina serve anche per campagne Google Ads?", answer: "Sì. Una landing locale come questa migliora la coerenza tra annuncio, parola chiave e contenuto, aiutando a ridurre sprechi di budget." },
    ],
  },
  "soccorso-stradale-messina": {
    slug: "soccorso-stradale-messina",
    path: "/soccorso-stradale-messina",
    schemaName: "Soccorso stradale a Messina H24",
    serviceType: "Soccorso stradale H24",
    eyebrow: "Soccorso stradale Messina",
    title: "Soccorso stradale a Messina H24 per auto, furgoni e mezzi da lavoro",
    description: "Viscar dedica questa pagina al soccorso stradale a Messina con assistenza H24, recupero, traino e supporto rapido nell’area di Tremestieri e nei collegamenti principali.",
    badges: ["Soccorso H24", "Messina", "Tremestieri", "Pronto intervento"],
    reasonsTitle: "Una pagina costruita per chi cerca assistenza subito",
    reasonsDescription: "Qui il focus non è solo descrivere il servizio, ma farsi trovare da chi scrive su Google soccorso stradale Messina e vuole una risposta concreta in pochi secondi.",
    highlights: [
      { icon: Clock3, title: "Intervento rapido", description: "La pagina mette al centro il contatto diretto, il pronto intervento e l’accesso immediato al servizio H24." },
      { icon: MapPin, title: "Presenza territoriale", description: "Il contenuto cita Messina, Tremestieri e le direttrici più importanti per rafforzare il posizionamento locale." },
      { icon: TriangleAlert, title: "Casi reali", description: "Guasto, incidente, fermo mezzo, impossibilità di ripartenza: scenari concreti che aumentano rilevanza e fiducia." },
      { icon: PhoneCall, title: "Contatto diretto", description: "Numero telefono e WhatsApp restano subito disponibili, senza passaggi inutili o percorsi complessi." },
    ],
    proofTitle: "Cosa cerca davvero chi digita soccorso stradale Messina",
    proofDescription: "L’utente spesso non vuole leggere pagine generiche: vuole capire se l’azienda è vicina, se risponde H24 e se può gestire subito il suo problema. Per questo il contenuto è più locale, più diretto e più orientato alla chiamata.",
    proofBullets: ["Assistenza per auto e veicoli commerciali", "Intervento rapido su viabilità urbana", "Supporto vicino allo svincolo di Tremestieri", "Contatto semplice senza percorsi complicati"],
    faqs: [
      { question: "Questa pagina è diversa dalla homepage?", answer: "Sì. La homepage resta più generale, mentre qui il servizio viene presentato in modo più diretto e concentrato su Messina e sull’assistenza H24." },
      { question: "È una pagina utile anche per chi arriva da mobile?", answer: "Sì. L’impostazione è pensata per rendere immediati telefono, WhatsApp e accesso ai punti principali del servizio." },
      { question: "Perché è importante nominare Tremestieri?", answer: "Perché è un riferimento geografico forte per chi si trova nella zona sud di Messina o nei pressi degli accessi autostradali." },
    ],
  },
  "soccorso-autostradale-a18-a20": {
    slug: "soccorso-autostradale-a18-a20",
    path: "/soccorso-autostradale-a18-a20",
    schemaName: "Soccorso autostradale A18 A20",
    serviceType: "Soccorso autostradale",
    eyebrow: "Soccorso autostradale A18 A20",
    title: "Soccorso autostradale su A18 e A20 con base a Messina",
    description: "Pagina dedicata al soccorso autostradale su A18 e A20, con attenzione alla vicinanza a Tremestieri, al coordinamento H24 e agli interventi sulle tratte principali.",
    badges: ["A18", "A20", "Tremestieri", "H24 Dispatch"],
    reasonsTitle: "Una pagina focalizzata sulle ricerche in autostrada",
    reasonsDescription: "Le ricerche legate ad A18 e A20 sono diverse da quelle urbane: il contenuto qui è stato pensato per intercettare chi cerca soccorso autostradale, uscita rapida e copertura reale dall’area Messina.",
    highlights: [
      { icon: MapPin, title: "Vicinanza agli svincoli", description: "La posizione a Tremestieri rafforza il messaggio verso chi si trova in ingresso o uscita dalle tratte autostradali." },
      { icon: Truck, title: "Supporto su tratte principali", description: "La pagina raccoglie i riferimenti operativi più utili per chi si trova su A18 o A20 e ha bisogno di assistenza." },
      { icon: ShieldCheck, title: "Lettura più chiara", description: "Un percorso dedicato all’autostrada rende più semplice capire subito se il servizio è adatto alla situazione." },
      { icon: Clock3, title: "Urgenza ben comunicata", description: "Il contenuto parla il linguaggio di chi ha un mezzo fermo in un tratto ad alto stress e deve contattare qualcuno subito." },
    ],
    proofTitle: "Perché una pagina dedicata all’autostrada è utile",
    proofDescription: "Se l’utente si trova in A18 o A20, una pagina troppo generica può sembrare lontana dalla sua necessità. Qui invece trova riferimenti precisi a tratte, svincoli e operatività H24 in un contesto più credibile.",
    proofBullets: ["Veicolo fermo su A18 o A20", "Richiesta di recupero verso area sicura", "Supporto per mezzi leggeri e commerciali", "Punto di contatto immediato via telefono o WhatsApp"],
    faqs: [
      { question: "Perché conviene avere una pagina dedicata ad A18 e A20?", answer: "Perché chi si trova in autostrada ha esigenze diverse da chi è in città, e qui trova un contesto più vicino alla sua situazione." },
      { question: "Questa pagina sostituisce quella principale del soccorso?", answer: "No. La integra e rende più chiaro il collegamento con l’autostrada e con i punti operativi principali." },
      { question: "È utile anche per chi arriva da mobile?", answer: "Sì. I contatti e le azioni principali restano immediati anche quando l’utente ha bisogno di aiuto veloce in movimento." },
      { question: "Il collegamento con Tremestieri resta visibile?", answer: "Sì. Tremestieri continua a essere trattata come base strategica vicina agli svincoli e ai raccordi principali." },
    ],
  },
  "soccorso-mezzi-pesanti-messina": {
    slug: "soccorso-mezzi-pesanti-messina",
    path: "/soccorso-mezzi-pesanti-messina",
    schemaName: "Soccorso mezzi pesanti a Messina",
    serviceType: "Heavy recovery e soccorso mezzi pesanti",
    eyebrow: "Soccorso mezzi pesanti Messina",
    title: "Soccorso mezzi pesanti a Messina per camion, autobus e semirimorchi",
    description: "Viscar raccoglie qui le informazioni dedicate al soccorso mezzi pesanti a Messina, con supporto per camion, autobus, motrici e semirimorchi su città, A18 e A20.",
    badges: ["Heavy Recovery", "Camion", "A18 / A20", "Messina"],
    reasonsTitle: "Più visibilità sulle ricerche truck e industriali",
    reasonsDescription: "Il settore mezzi pesanti ha dinamiche diverse dal soccorso auto. Una landing dedicata consente di parlare in modo più tecnico, più verticale e più credibile verso aziende, autisti e trasportatori.",
    highlights: [
      { icon: Truck, title: "Heavy recovery", description: "La pagina è dedicata a camion, autobus, motrici, semirimorchi e recuperi complessi con un’impostazione più specifica." },
      { icon: TriangleAlert, title: "Fermo mezzo e incidenti", description: "Il copy copre casi ad alta criticità: ribaltamenti, mezzi bloccati, necessità di recupero o supporto tecnico su strada." },
      { icon: ShieldCheck, title: "Più autorevolezza", description: "Un contenuto dedicato ai mezzi pesanti rafforza il posizionamento di Viscar anche agli occhi di chi valuta l’affidabilità prima di chiamare." },
      { icon: PhoneCall, title: "Contatto rapido", description: "Telefono e WhatsApp restano sempre vicini ai contenuti principali per semplificare il contatto in caso di urgenza." },
    ],
    proofTitle: "Perché una pagina dedicata ai mezzi pesanti è utile",
    proofDescription: "Quando si mandano tutti gli utenti sulla stessa pagina generica, le campagne si allargano troppo. Una pagina truck separata aiuta a filtrare meglio il traffico e a far arrivare richieste più qualificate.",
    proofBullets: ["Camion, autobus e motrici", "Semirimorchi e mezzi industriali", "Recuperi complessi e supporto gru", "Copertura Messina, A18 e A20"],
    faqs: [
      { question: "La pagina è adatta anche per aziende di trasporto?", answer: "Sì. Il linguaggio e la struttura sono pensati anche per chi gestisce flotte, logistica o veicoli industriali." },
      { question: "Perché separare i mezzi pesanti dal soccorso generico?", answer: "Perché chi gestisce camion, autobus o semirimorchi ha esigenze più specifiche e beneficia di una pagina costruita attorno a quel contesto." },
      { question: "Questa pagina mantiene contatti rapidi e accesso diretto?", answer: "Sì. Anche qui telefono e WhatsApp restano ben visibili e facili da raggiungere." },
    ],
  },
  "soccorso-stradale-provincia-di-messina": {
    slug: "soccorso-stradale-provincia-di-messina",
    path: "/soccorso-stradale-provincia-di-messina",
    schemaName: "Soccorso stradale in provincia di Messina",
    serviceType: "Soccorso stradale in provincia di Messina",
    eyebrow: "Messina e provincia",
    title: "Soccorso stradale in provincia di Messina con operatività H24",
    description: "Viscar collega la base di Messina alla provincia con un servizio di soccorso stradale H24 pensato per viabilità urbana, collegamenti autostradali e aree operative più sensibili del territorio.",
    badges: ["Provincia di Messina", "H24", "A18 / A20", "Operatività"],
    reasonsTitle: "Copertura territoriale più ampia",
    reasonsDescription: "Questa pagina riunisce in modo diretto le informazioni utili per chi cerca assistenza nella provincia di Messina, mantenendo riferimenti chiari a strade, collegamenti e aree più rilevanti.",
    highlights: [
      { icon: MapPin, title: "Presenza sul territorio", description: "Messina resta il punto di partenza operativo, con attenzione anche alle aree provinciali e ai collegamenti più usati." },
      { icon: Clock3, title: "Pronto intervento", description: "Assistenza continua per guasti, incidenti, fermo mezzo e richieste operative distribuite sul territorio." },
      { icon: Truck, title: "Tipologie di veicoli", description: "Auto, furgoni, mezzi commerciali e supporto ai casi che richiedono recovery o traino dedicato." },
      { icon: ShieldCheck, title: "Contatto diretto", description: "Numeri e accessi rapidi per chi ha bisogno di contattare subito il servizio senza passaggi inutili." },
    ],
    proofTitle: "Un punto di riferimento per Messina e per la provincia",
    proofDescription: "Quando il bisogno non riguarda solo la città ma un’area più estesa, è utile trovare una pagina che raccolga in modo ordinato copertura, contatti e contesto operativo senza creare confusione.",
    proofBullets: ["Messina città e collegamenti provinciali", "Aree vicine agli svincoli principali", "Supporto per viabilità urbana ed extraurbana", "Contatto rapido via telefono o WhatsApp"],
    faqs: [
      { question: "Viscar lavora solo a Messina città?", answer: "No. La comunicazione del sito comprende anche la provincia di Messina e i collegamenti più rilevanti verso le aree limitrofe." },
      { question: "Quali aree vengono richiamate più spesso?", answer: "Messina, Tremestieri, A18, A20 e diversi punti della provincia che richiedono un riferimento chiaro e rapido per il soccorso." },
      { question: "Questa pagina sostituisce quella principale del soccorso?", answer: "No. Serve come accesso territoriale più ampio, mentre la pagina principale resta il riferimento operativo completo del servizio H24." },
      { question: "Posso usarla per capire subito come contattare Viscar?", answer: "Sì. L’impostazione della pagina mantiene in evidenza i contatti e i percorsi rapidi verso chiamata e WhatsApp." },
    ],
  },
  "carroattrezzi-tremestieri": {
    slug: "carroattrezzi-tremestieri",
    path: "/carroattrezzi-tremestieri",
    schemaName: "Carroattrezzi a Tremestieri",
    serviceType: "Carroattrezzi e soccorso stradale a Tremestieri",
    eyebrow: "Tremestieri",
    title: "Carroattrezzi a Tremestieri e accesso rapido alla viabilità principale",
    description: "Viscar valorizza l’area di Tremestieri come snodo operativo strategico per chi cerca carroattrezzi, soccorso stradale H24 e supporto rapido vicino agli svincoli e ai collegamenti principali.",
    badges: ["Tremestieri", "Svincolo", "H24", "Carroattrezzi"],
    reasonsTitle: "Un punto strategico per l’intervento rapido",
    reasonsDescription: "Tremestieri è uno dei riferimenti più forti del sito perché collega città, autostrada e area operativa. Qui la lettura è più immediata per chi si trova vicino agli svincoli o in movimento verso Messina.",
    highlights: [
      { icon: MapPin, title: "Vicinanza agli svincoli", description: "Contesto utile per chi cerca un riferimento operativo vicino ai nodi di accesso più sensibili della zona sud di Messina." },
      { icon: Truck, title: "Traino e assistenza", description: "Supporto dedicato a guasti, fermo mezzo, recupero e trasporto verso area sicura o officina." },
      { icon: Clock3, title: "Pronto intervento H24", description: "Una pagina costruita per restare semplice, rapida e chiara anche quando l’urgenza è alta." },
      { icon: ShieldCheck, title: "Copertura coerente", description: "Tremestieri dialoga naturalmente con Messina, A18 e A20 senza appesantire la navigazione." },
    ],
    proofTitle: "Perché Tremestieri conta davvero",
    proofDescription: "Chi si trova in questa zona cerca di solito una risposta immediata e un operatore che conosca bene gli accessi, i raccordi e i tempi di uscita. Per questo Tremestieri merita una pagina dedicata e autonoma.",
    proofBullets: ["Area sud di Messina", "Vicinanza agli svincoli", "Accesso rapido a città e autostrada", "Contatto diretto con Viscar"],
    faqs: [
      { question: "Perché Viscar dedica una pagina a Tremestieri?", answer: "Perché è un punto operativo importante e riconoscibile per chi si muove tra Messina, raccordi e autostrada." },
      { question: "Questa pagina è utile solo per i residenti della zona?", answer: "No. È utile anche a chi attraversa Tremestieri e cerca un contatto rapido in caso di fermo mezzo o guasto improvviso." },
      { question: "Tremestieri è collegata anche ad A18 e A20?", answer: "Sì. Proprio per questo nel sito viene trattata come area strategica e non come riferimento secondario." },
      { question: "Il contatto è lo stesso del soccorso principale?", answer: "Sì. La pagina mantiene lo stesso accesso rapido al servizio operativo H24 di Viscar." },
    ],
  },
  "carroattrezzi-villafranca-tirrena": {
    slug: "carroattrezzi-villafranca-tirrena",
    path: "/carroattrezzi-villafranca-tirrena",
    schemaName: "Carroattrezzi a Villafranca Tirrena",
    serviceType: "Carroattrezzi e soccorso stradale a Villafranca Tirrena",
    eyebrow: "Villafranca Tirrena",
    title: "Carroattrezzi a Villafranca Tirrena con collegamento rapido verso Messina",
    description: "Viscar dedica una pagina a chi cerca carroattrezzi a Villafranca Tirrena, con supporto H24, traino, recupero e accesso diretto ai contatti per richieste urgenti nell’area ovest verso Messina.",
    badges: ["Villafranca Tirrena", "Carroattrezzi", "H24", "Messina"],
    reasonsTitle: "Un accesso chiaro per l’area ovest",
    reasonsDescription: "Villafranca Tirrena ha una domanda più specifica rispetto al solo centro di Messina. Per questo qui il servizio è raccontato in modo più locale, semplice e diretto, senza allargarsi inutilmente.",
    highlights: [
      { icon: MapPin, title: "Villafranca e collegamenti", description: "Pagina utile per chi cerca un riferimento chiaro tra Villafranca Tirrena, Messina e i collegamenti principali dell’area ovest." },
      { icon: Truck, title: "Traino e recupero", description: "Carroattrezzi per auto, furgoni e veicoli da lavoro con attenzione ai casi di fermo mezzo e guasto improvviso." },
      { icon: Clock3, title: "Operatività H24", description: "Contatti rapidi e struttura del contenuto pensata per chi ha bisogno di una risposta immediata." },
      { icon: ShieldCheck, title: "Percorso dedicato", description: "Un accesso locale separato rende il sito più ordinato e più utile per chi cerca assistenza in quest’area." },
    ],
    proofTitle: "Perché Villafranca Tirrena merita una pagina dedicata",
    proofDescription: "Quando la ricerca riguarda Villafranca Tirrena, una pagina locale aiuta a mostrare subito copertura, contatto e vicinanza operativa senza costringere l’utente a passare da informazioni troppo generiche.",
    proofBullets: ["Villafranca Tirrena e area ovest", "Traino e recupero H24", "Collegamento operativo con Messina", "Contatto diretto via telefono o WhatsApp"],
    faqs: [
      { question: "Viscar può essere un riferimento anche per Villafranca Tirrena?", answer: "Sì. Questa pagina serve proprio a dare un accesso più diretto a chi cerca carroattrezzi e assistenza in quell’area." },
      { question: "La pagina è utile solo per carroattrezzi?", answer: "È pensata soprattutto per traino e recupero, ma richiama anche l’assistenza stradale H24 quando il bisogno è urgente." },
      { question: "Villafranca Tirrena è collegata bene con la base operativa di Messina?", answer: "Sì. Per questo il sito può trattarla come area locale dedicata senza uscire dal perimetro operativo di Viscar." },
      { question: "I contatti sono gli stessi del soccorso principale?", answer: "Sì. La pagina mantiene gli stessi accessi rapidi a chiamata e WhatsApp del servizio principale." },
    ],
  },
  "soccorso-stradale-milazzo": {
    slug: "soccorso-stradale-milazzo",
    path: "/soccorso-stradale-milazzo",
    schemaName: "Soccorso stradale a Milazzo",
    serviceType: "Soccorso stradale a Milazzo",
    eyebrow: "Milazzo",
    title: "Soccorso stradale a Milazzo con collegamento operativo a Messina",
    description: "Pagina dedicata a chi cerca soccorso stradale a Milazzo con riferimento operativo chiaro, copertura H24 e collegamento naturale con Messina, provincia e viabilità principale.",
    badges: ["Milazzo", "H24", "Provincia", "Assistenza"],
    reasonsTitle: "Un accesso dedicato per l’area nord",
    reasonsDescription: "Milazzo è una località rilevante nelle ricerche provinciali. Questa pagina offre un percorso più chiaro e specifico senza appesantire la struttura generale del sito.",
    highlights: [
      { icon: MapPin, title: "Area nord della provincia", description: "Milazzo viene trattata come nodo autonomo per facilitare la lettura locale e il contatto rapido." },
      { icon: Clock3, title: "Assistenza H24", description: "Interventi legati a guasti, fermo veicolo, recuperi e richieste operative urgenti." },
      { icon: Truck, title: "Più tipologie di mezzi", description: "Supporto per auto, veicoli commerciali e casi che richiedono traino o recovery dedicato." },
      { icon: ShieldCheck, title: "Struttura semplice", description: "La pagina evita frasi artificiose e concentra le informazioni davvero utili a chi cerca aiuto." },
    ],
    proofTitle: "Milazzo come area di accesso locale",
    proofDescription: "Per chi cerca assistenza in quest’area è utile trovare una pagina dedicata, senza dover passare per contenuti troppo generici o troppo centrati solo sulla città di Messina.",
    proofBullets: ["Milazzo e aree vicine", "Supporto H24", "Collegamento con Messina e provincia", "Contatti diretti e immediati"],
    faqs: [
      { question: "Viscar può essere rilevante anche per chi cerca soccorso stradale a Milazzo?", answer: "Sì. La pagina è stata pensata proprio per dare un accesso più diretto a chi cerca un riferimento su Milazzo e sulla provincia." },
      { question: "Milazzo viene trattata come area separata?", answer: "Sì. Avere un accesso dedicato aiuta a rendere più leggibile il servizio e più chiaro il contesto operativo." },
      { question: "La pagina mantiene gli stessi contatti rapidi?", answer: "Sì. Anche qui restano in evidenza telefono e WhatsApp per raggiungere subito il servizio." },
      { question: "Questa pagina sostituisce il soccorso principale?", answer: "No. È un approfondimento territoriale che rafforza la copertura del sito senza cambiare il cuore operativo di Viscar." },
    ],
  },
  "soccorso-stradale-taormina": {
    slug: "soccorso-stradale-taormina",
    path: "/soccorso-stradale-taormina",
    schemaName: "Soccorso stradale a Taormina",
    serviceType: "Soccorso stradale a Taormina",
    eyebrow: "Taormina",
    title: "Soccorso stradale a Taormina e collegamenti con A18 e Messina",
    description: "Viscar dedica una pagina a Taormina per offrire un accesso più chiaro a chi cerca soccorso stradale H24, supporto rapido e riferimenti operativi verso A18 e Messina.",
    badges: ["Taormina", "A18", "H24", "Pronto intervento"],
    reasonsTitle: "Un riferimento più chiaro per l’area sud",
    reasonsDescription: "Taormina è una località importante nei collegamenti con l’autostrada e con Messina. Questa pagina rende più immediata la navigazione per chi cerca assistenza in quell’area.",
    highlights: [
      { icon: MapPin, title: "Taormina e collegamenti", description: "Pagina dedicata a una delle zone più rilevanti nei percorsi tra costa, A18 e Messina." },
      { icon: Clock3, title: "Assistenza continua", description: "Supporto H24 per guasti, fermo mezzo, recuperi e richieste operative di pronto intervento." },
      { icon: Truck, title: "Viabilità principale", description: "La presenza di A18 e dei collegamenti limitrofi rende utile un percorso dedicato a quest’area." },
      { icon: ShieldCheck, title: "Contatti rapidi", description: "Accesso diretto al numero e a WhatsApp senza dover cercare le informazioni in altre sezioni del sito." },
    ],
    proofTitle: "Taormina come area di accesso dedicata",
    proofDescription: "Quando l’utente cerca assistenza su Taormina, avere una pagina più locale aiuta a capire subito che il sito conosce bene l’area, i collegamenti e le necessità operative più comuni.",
    proofBullets: ["Taormina e collegamenti verso Messina", "Riferimento ad A18", "Pronto intervento H24", "Accesso rapido a telefono e WhatsApp"],
    faqs: [
      { question: "Perché dedicare una pagina a Taormina?", answer: "Perché è una località chiave nei collegamenti con A18 e nella lettura territoriale dell’area servita da Viscar." },
      { question: "La pagina è utile anche per chi viaggia in autostrada?", answer: "Sì. Il collegamento con A18 rende Taormina un punto importante per chi cerca assistenza in movimento tra costa e autostrada." },
      { question: "I contatti sono immediati anche qui?", answer: "Sì. La pagina mantiene lo stesso accesso rapido a chiamata e WhatsApp del servizio principale." },
      { question: "Questa pagina cambia la struttura generale del sito?", answer: "No. È un’area territoriale in più che amplia la copertura mantenendo una navigazione semplice e pulita." },
    ],
  },
};

const RoadsidePageShell = ({ children, testId }) => (
  <main data-testid={testId} className="viscar-site viscar-site--inner">
    <Navbar />
    {children}
    <Footer />
    <MobileEmergencyBar />
  </main>
);

export const LocalSeoLinkSection = () => (
  <section data-testid="local-seo-link-section" className="section">
    <div className="container">
      <div data-testid="local-seo-link-header" className="section-header section-header--wide">
        <span data-testid="local-seo-link-eyebrow" className="section-eyebrow">Aree dedicate</span>
        <h2 data-testid="local-seo-link-title">Approfondimenti dedicati ai principali servizi Viscar</h2>
        <p data-testid="local-seo-link-description">Percorsi dedicati per soccorso stradale, autostrada, mezzi pesanti e richieste operative nell’area di Messina.</p>
      </div>
      <div data-testid="local-seo-link-grid" className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {LOCAL_SEO_LINKS.map((item, index) => (
          <article data-testid={`local-seo-link-card-${item.slug}`} key={item.slug} className="tech-card flex flex-col gap-4 p-6">
            <span data-testid={`local-seo-link-card-badge-${item.slug}`} className="card-tag w-fit">{item.badge}</span>
            <h3 data-testid={`local-seo-link-card-title-${item.slug}`}>{item.label}</h3>
            <p data-testid={`local-seo-link-card-description-${item.slug}`}>{item.description}</p>
            <Button data-testid={`local-seo-link-card-action-${item.slug}`} asChild className="btn-ghost-tech mt-auto">
              <Link to={item.path}>Apri pagina <ArrowRight size={16} /></Link>
            </Button>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const TerritoryLandingSection = () => (
  <section data-testid="territory-landing-section" className="section">
    <div className="container">
      <div data-testid="territory-landing-header" className="section-header section-header--wide">
        <span data-testid="territory-landing-eyebrow" className="section-eyebrow">Territori serviti</span>
        <h2 data-testid="territory-landing-title">Percorsi dedicati per Messina, provincia e aree operative principali</h2>
        <p data-testid="territory-landing-description">Accessi ordinati per città, provincia, Tremestieri e collegamenti principali, così il sito resta chiaro anche quando il contesto cambia.</p>
      </div>
      <div data-testid="territory-landing-grid" className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {LOCAL_AREA_LINKS.map((item) => (
          <article data-testid={`territory-landing-card-${item.slug}`} key={item.slug} className="tech-card flex flex-col gap-4 p-6">
            <span data-testid={`territory-landing-badge-${item.slug}`} className="card-tag w-fit">{item.badge}</span>
            <h3 data-testid={`territory-landing-title-${item.slug}`}>{item.label}</h3>
            <p data-testid={`territory-landing-copy-${item.slug}`}>{item.description}</p>
            <Button data-testid={`territory-landing-link-${item.slug}`} asChild className="btn-ghost-tech mt-auto">
              <Link to={item.path}>Apri pagina <ArrowRight size={16} /></Link>
            </Button>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const HomeSeoBoostSection = () => {
  const intents = [
    {
      slug: "carroattrezzi-messina",
      title: "Carroattrezzi Messina",
      copy: "Servizio dedicato a traino, recupero e assistenza su Messina e zone vicine.",
      href: "/carroattrezzi-messina",
    },
    {
      slug: "soccorso-stradale-messina",
      title: "Soccorso stradale Messina",
      copy: "Pagina dedicata agli interventi H24 su viabilità urbana, raccordi e zone operative principali.",
      href: "/soccorso-stradale-messina",
    },
    {
      slug: "soccorso-autostradale-a18-a20",
      title: "Soccorso autostradale A18 e A20",
      copy: "Approfondimento dedicato agli interventi in autostrada e nelle aree vicine agli svincoli principali.",
      href: "/soccorso-autostradale-a18-a20",
    },
    {
      slug: "soccorso-mezzi-pesanti-messina",
      title: "Soccorso mezzi pesanti Messina",
      copy: "Percorso dedicato a camion, autobus, motrici, semirimorchi e interventi complessi.",
      href: "/soccorso-mezzi-pesanti-messina",
    },
  ];

  const faqs = [
    {
      question: "Viscar lavora come carroattrezzi e soccorso stradale H24 a Messina?",
      answer: "Sì, Viscar opera su Messina con soccorso stradale H24, recovery, supporto tecnico e assistenza operativa nei punti più strategici dell’area servita.",
    },
    {
      question: "Perché ci sono pagine separate per Messina, A18/A20 e mezzi pesanti?",
      answer: "Per offrire percorsi più chiari e immediati a seconda del tipo di intervento richiesto, senza mescolare esigenze diverse nella stessa pagina.",
    },
    {
      question: "Quali aree copre principalmente Viscar?",
      answer: "Messina, Tremestieri e i collegamenti principali verso A18 e A20 restano il cuore operativo comunicato dal sito.",
    },
    {
      question: "Com’è organizzata la navigazione del sito?",
      answer: "La navigazione separa in modo semplice soccorso H24, meccatronica, gommista e contatti, così ogni percorso resta più leggibile e diretto.",
    },
  ];

  return (
    <>
      <section data-testid="home-seo-boost-section" className="section">
        <div className="container">
          <div data-testid="home-seo-boost-header" className="section-header section-header--wide">
            <span data-testid="home-seo-boost-eyebrow" className="section-eyebrow">Percorsi rapidi</span>
            <h2 data-testid="home-seo-boost-title">Servizi organizzati in aree chiare e immediate</h2>
            <p data-testid="home-seo-boost-description">Ogni accesso porta più velocemente al servizio giusto, mantenendo una lettura semplice e concreta del sito.</p>
          </div>
          <div data-testid="home-seo-boost-grid" className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {intents.map((item, index) => (
              <article data-testid={`home-seo-boost-card-${item.slug}`} key={item.slug} className="tech-card flex flex-col gap-4 p-6">
                <span data-testid={`home-seo-boost-card-tag-${item.slug}`} className="card-tag w-fit">Servizio</span>
                <h3 data-testid={`home-seo-boost-card-title-${item.slug}`}>{item.title}</h3>
                <p data-testid={`home-seo-boost-card-copy-${item.slug}`}>{item.copy}</p>
                <Button data-testid={`home-seo-boost-card-link-${item.slug}`} asChild className="btn-ghost-tech mt-auto">
                  <Link to={item.href}>Apri pagina <ArrowRight size={16} /></Link>
                </Button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-seo-faq-section" className="section">
        <div className="container">
          <div data-testid="home-seo-faq-header" className="section-header section-header--wide">
            <span data-testid="home-seo-faq-eyebrow" className="section-eyebrow">Domande frequenti</span>
            <h2 data-testid="home-seo-faq-title">Informazioni rapide sui servizi e sulla copertura</h2>
            <p data-testid="home-seo-faq-description">Un riepilogo essenziale per chiarire in modo diretto i punti più utili a chi visita il sito.</p>
          </div>
          <div data-testid="home-seo-faq-grid" className="grid gap-6 md:grid-cols-2">
            {faqs.map((item, index) => (
              <article data-testid={`home-seo-faq-card-${index}`} key={item.question} className="tech-card flex flex-col gap-4 p-6">
                <h3 data-testid={`home-seo-faq-question-${index}`}>{item.question}</h3>
                <p data-testid={`home-seo-faq-answer-${index}`}>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const LocalSeoPage = ({ pageKey }) => {
  const page = LOCAL_SEO_PAGES[pageKey];
  const relatedLinks = [...LOCAL_SEO_LINKS, ...LOCAL_AREA_LINKS].filter((item) => item.slug !== page.slug);

  useEffect(() => {
    upsertStructuredData("viscar-local-breadcrumb-schema", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: page.schemaName,
          item: `${BASE_URL}${page.path}`,
        },
      ],
    });

    upsertStructuredData("viscar-local-service-schema", {
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.schemaName,
      serviceType: page.serviceType,
      description: page.description,
      areaServed: ["Messina", "Tremestieri", "A18", "A20"],
      url: `${BASE_URL}${page.path}`,
      provider: {
        "@type": "AutomotiveBusiness",
        name: "Viscar",
        url: BASE_URL,
        telephone: "+39 3517523353",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Messina",
          addressCountry: "IT",
          streetAddress: "Larderia – Polo ASI, Capannone 18",
        },
      },
    });

    upsertStructuredData("viscar-local-faq-schema", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });

    return () => {
      removeStructuredData("viscar-local-breadcrumb-schema");
      removeStructuredData("viscar-local-service-schema");
      removeStructuredData("viscar-local-faq-schema");
    };
  }, [page]);

  return (
    <RoadsidePageShell testId={`${page.slug}-page`}>
      <section data-testid={`${page.slug}-hero`} className="section division-hero" style={{ "--division-hero-image": `url(${seoHeroImage})` }}>
        <div className="division-hero__overlay" />
        <div className="container division-hero__content">
          <div data-testid={`${page.slug}-hero-copy`} className="division-hero__copy">
            <EditableText textId={`local.${page.slug}.hero.eyebrow`} defaultText={page.eyebrow} as="span" className="section-eyebrow" testId={`${page.slug}-hero-eyebrow`} />
            <EditableText textId={`local.${page.slug}.hero.title`} defaultText={page.title} as="h1" testId={`${page.slug}-hero-title`} />
            <EditableText textId={`local.${page.slug}.hero.description`} defaultText={page.description} as="p" testId={`${page.slug}-hero-description`} multiline />
            <div data-testid={`${page.slug}-hero-badges`} className="badge-row badge-row--large">
              {page.badges.map((badge, index) => (
                <span data-testid={`${page.slug}-hero-badge-${index}`} key={badge}>{badge}</span>
              ))}
            </div>
            <div data-testid={`${page.slug}-hero-actions`} className="section-actions">
              <Button data-testid={`${page.slug}-hero-call`} asChild className="btn-emergency">
                <a href={CONTACT.roadsideHref}><PhoneCall size={18} /> Chiama ora</a>
              </Button>
              <Button data-testid={`${page.slug}-hero-whatsapp`} asChild className="btn-whatsapp">
                <a href={getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, CONTACT.roadsideWhatsAppNumber)} target="_blank" rel="noreferrer"><MessageCircle size={18} /> WhatsApp H24</a>
              </Button>
              <Button data-testid={`${page.slug}-hero-contacts`} asChild className="btn-ghost-tech">
                <Link to="/contatti">Contatti Viscar <ArrowRight size={16} /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section data-testid={`${page.slug}-reasons-section`} className="section">
        <div className="container">
          <div data-testid={`${page.slug}-reasons-header`} className="section-header section-header--wide">
            <EditableText textId={`local.${page.slug}.reasons.title`} defaultText={page.reasonsTitle.replace("Perché questa pagina intercetta ricerche ad alta urgenza", "Punti chiave del servizio").replace("Una pagina costruita per chi cerca assistenza subito", "Cosa trovi in questa area").replace("Una pagina focalizzata sulle ricerche in autostrada", "Supporto dedicato all’autostrada").replace("Più visibilità sulle ricerche truck e industriali", "Supporto dedicato ai mezzi pesanti")} as="h2" testId={`${page.slug}-reasons-title`} />
            <EditableText textId={`local.${page.slug}.reasons.description`} defaultText={page.reasonsDescription.replace("Chi cerca carroattrezzi di solito ha bisogno di una risposta immediata, di un numero chiaro e di un’azienda vicina. Qui Viscar si presenta con un messaggio semplice, locale e orientato alla chiamata.", "Questa sezione riassume in modo diretto l’impostazione del servizio, il contesto operativo e i punti utili per chi deve contattare Viscar rapidamente.").replace("Qui il focus non è solo descrivere il servizio, ma farsi trovare da chi scrive su Google soccorso stradale Messina e vuole una risposta concreta in pochi secondi.", "Qui trovi un quadro sintetico del servizio, della copertura operativa e dei contatti diretti per l’assistenza.").replace("Le ricerche legate ad A18 e A20 sono diverse da quelle urbane: il contenuto qui è stato pensato per intercettare chi cerca soccorso autostradale, uscita rapida e copertura reale dall’area Messina.", "Questa area è dedicata agli interventi in autostrada, con riferimenti chiari a tratte, svincoli e operatività H24.").replace("Il settore mezzi pesanti ha dinamiche diverse dal soccorso auto. Una landing dedicata consente di parlare in modo più tecnico, più verticale e più credibile verso aziende, autisti e trasportatori.", "Questa pagina raccoglie in modo più ordinato le informazioni dedicate a camion, autobus, motrici, semirimorchi e recuperi complessi.")} as="p" testId={`${page.slug}-reasons-description`} multiline />
          </div>
          <div data-testid={`${page.slug}-reasons-grid`} className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {page.highlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <article data-testid={`${page.slug}-reason-card-${index}`} key={item.title} className="tech-card flex flex-col gap-4 p-6">
                  <Icon data-testid={`${page.slug}-reason-icon-${index}`} className="service-icon" size={28} />
                  <EditableText textId={`local.${page.slug}.reason.${index}.title`} defaultText={item.title} as="h3" testId={`${page.slug}-reason-title-${index}`} />
                  <EditableText textId={`local.${page.slug}.reason.${index}.description`} defaultText={item.description} as="p" testId={`${page.slug}-reason-description-${index}`} multiline />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid={`${page.slug}-proof-section`} className="section">
        <div className="container grid gap-6 lg:grid-cols-[1.3fr_0.9fr]">
          <div data-testid={`${page.slug}-proof-copy`} className="tech-card flex flex-col gap-5 p-6 lg:p-8">
            <span data-testid={`${page.slug}-proof-eyebrow`} className="section-eyebrow">Operatività</span>
            <EditableText textId={`local.${page.slug}.proof.title`} defaultText={page.proofTitle} as="h2" testId={`${page.slug}-proof-title`} />
            <EditableText textId={`local.${page.slug}.proof.description`} defaultText={page.proofDescription} as="p" testId={`${page.slug}-proof-description`} multiline />
            <div data-testid={`${page.slug}-proof-actions`} className="section-actions">
              <Button data-testid={`${page.slug}-proof-call`} asChild className="btn-emergency">
                <a href={CONTACT.roadsideHref}><PhoneCall size={18} /> Chiama soccorso</a>
              </Button>
              <Button data-testid={`${page.slug}-proof-whatsapp`} asChild className="btn-ghost-tech">
                <a href={getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, CONTACT.roadsideWhatsAppNumber)} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Apri WhatsApp</a>
              </Button>
            </div>
          </div>
          <aside data-testid={`${page.slug}-proof-bullets`} className="tech-card flex flex-col gap-4 p-6 lg:p-8">
            <span data-testid={`${page.slug}-proof-badge`} className="card-tag w-fit">Servizio</span>
            {page.proofBullets.map((bullet, index) => (
              <div data-testid={`${page.slug}-proof-bullet-${index}`} key={bullet} className="flex items-start gap-3">
                <ShieldCheck className="mt-1 shrink-0 text-[var(--brand-warning)]" size={18} />
                <span>{bullet}</span>
              </div>
            ))}
          </aside>
        </div>
      </section>

      <section data-testid={`${page.slug}-faq-section`} className="section">
        <div className="container">
          <div data-testid={`${page.slug}-faq-header`} className="section-header section-header--wide">
            <span data-testid={`${page.slug}-faq-eyebrow`} className="section-eyebrow">Domande frequenti</span>
            <h2 data-testid={`${page.slug}-faq-title`}>Informazioni utili su questo servizio</h2>
            <p data-testid={`${page.slug}-faq-description`}>Risposte rapide ai dubbi più comuni per rendere la consultazione più semplice e immediata.</p>
          </div>
          <div data-testid={`${page.slug}-faq-grid`} className="grid gap-6 md:grid-cols-2">
            {page.faqs.map((item, index) => (
              <article data-testid={`${page.slug}-faq-card-${index}`} key={item.question} className="tech-card flex flex-col gap-4 p-6">
                <EditableText textId={`local.${page.slug}.faq.${index}.question`} defaultText={item.question} as="h3" testId={`${page.slug}-faq-question-${index}`} />
                <EditableText textId={`local.${page.slug}.faq.${index}.answer`} defaultText={item.answer} as="p" testId={`${page.slug}-faq-answer-${index}`} multiline />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section data-testid={`${page.slug}-related-section`} className="section">
        <div className="container">
          <div data-testid={`${page.slug}-related-header`} className="section-header section-header--wide">
            <span data-testid={`${page.slug}-related-eyebrow`} className="section-eyebrow">Altri accessi utili</span>
            <h2 data-testid={`${page.slug}-related-title`}>Altre aree Viscar collegate</h2>
            <p data-testid={`${page.slug}-related-description`}>Collegamenti rapidi verso altri percorsi operativi del sito, così la navigazione resta più ordinata e leggibile.</p>
          </div>
          <div data-testid={`${page.slug}-related-grid`} className="grid gap-6 md:grid-cols-3">
            {relatedLinks.map((item) => (
              <article data-testid={`${page.slug}-related-card-${item.slug}`} key={item.slug} className="tech-card flex flex-col gap-4 p-6">
                <span data-testid={`${page.slug}-related-badge-${item.slug}`} className="card-tag w-fit">{item.badge}</span>
                <h3 data-testid={`${page.slug}-related-title-${item.slug}`}>{item.label}</h3>
                <p data-testid={`${page.slug}-related-copy-${item.slug}`}>{item.description}</p>
                <Button data-testid={`${page.slug}-related-link-${item.slug}`} asChild className="btn-ghost-tech mt-auto">
                  <Link to={item.path}>Apri pagina <ArrowRight size={16} /></Link>
                </Button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </RoadsidePageShell>
  );
};

export const CarroattrezziMessinaPage = () => <LocalSeoPage pageKey="carroattrezzi-messina" />;

export const SoccorsoStradaleMessinaPage = () => <LocalSeoPage pageKey="soccorso-stradale-messina" />;

export const SoccorsoAutostradalePage = () => <LocalSeoPage pageKey="soccorso-autostradale-a18-a20" />;

export const SoccorsoMezziPesantiPage = () => <LocalSeoPage pageKey="soccorso-mezzi-pesanti-messina" />;

export const SoccorsoProvinciaMessinaPage = () => <LocalSeoPage pageKey="soccorso-stradale-provincia-di-messina" />;

export const CarroattrezziTremestieriPage = () => <LocalSeoPage pageKey="carroattrezzi-tremestieri" />;

export const CarroattrezziVillafrancaPage = () => <LocalSeoPage pageKey="carroattrezzi-villafranca-tirrena" />;

export const SoccorsoStradaleMilazzoPage = () => <LocalSeoPage pageKey="soccorso-stradale-milazzo" />;

export const SoccorsoStradaleTaorminaPage = () => <LocalSeoPage pageKey="soccorso-stradale-taormina" />;